import { Close, InsertDriveFileOutlined, UploadOutlined } from '@mui/icons-material'
import { Box, FormControl, FormHelperText, IconButton, Stack, Typography, useMediaQuery, Button, Link } from '@mui/material'
import React, { FunctionComponent, ReactElement, SetStateAction } from 'react'
import gaiaTheme from '../../../../root-components/theme'
import { TooltipButton } from '../../miscellaneous-components/tooltip-button'
import { FileUploadHelper } from '../../miscellaneous-components/file-upload-helper'
import FormEditButtons from './form-edit-buttons'
import { UploadedResourcePreview } from './uploaded-resource-preview'
import UploadedResourceService from '../../../../shared/services/uploaded-resource/uploaded-resource.service'

export const FormFileInputDisplay: FunctionComponent<{
  isEditPage: boolean,
  allowEdit?: boolean,
  editField?: string | undefined,
  setEditField?: React.Dispatch<SetStateAction<any>> | undefined,
  hideEditIcon?: boolean,
  disabled: boolean,
  isLastField?: boolean,
  title: string,
  name: string,
  value: string | undefined,
  touched?: any,
  errors?: any,
  onChange: any,
  onImageUpload?: () => void,
  onImageRemove?: () => void,
  onImageLibrarySelect?: () => void,
  cancelEditHandler?: () => void,
  customErrorMessage?: string,
  tooltip?: string,
  imageWidth?: number,
  imageHeight?: number,
  uploadButtonText?: string,
  showImageLibraryButton?: boolean,
  imageLibraryButtonText?: string,
  placeholderIcon?: ReactElement,
  fileData?: any,
  setFileData?: React.Dispatch<SetStateAction<any>>,
  onFileUploadSubmit?: () => void,
  disableUploadButton?: boolean,
  hideSaveButton?: boolean,
  hidePreview?: boolean,
  allowedFileTypes?: string[],
  bottomBorder?: boolean,
  topBorder?: boolean,
  sideBorders?: boolean,
  isTopRow?: boolean,
  isBottomRow?: boolean,
  uploadedResources?: any[],
  removedResourceIds?: string[],
  onToggleResourceRemove?: (id: string, removed: boolean) => void,
}> = ({
  isEditPage,
  allowEdit = true,
  editField,
  setEditField,
  hideEditIcon = false,
  disabled,
  isLastField = false,
  title,
  name,
  value,
  touched,
  errors,
  onChange,
  onImageUpload,
  onImageRemove,
  onImageLibrarySelect,
  cancelEditHandler,
  customErrorMessage,
  tooltip,
  imageWidth = 75,
  imageHeight = 75,
  uploadButtonText = 'Upload File',
  showImageLibraryButton = false,
  imageLibraryButtonText = 'Choose from Image Library',
  placeholderIcon,
  fileData,
  setFileData,
  onFileUploadSubmit,
  disableUploadButton = false,
  hideSaveButton = false,
  hidePreview = false,
  allowedFileTypes,
  bottomBorder = false,
  topBorder = true,
  sideBorders = true,
  isTopRow = false,
  isBottomRow = false,
  uploadedResources,
  removedResourceIds,
  onToggleResourceRemove,
}) => {

    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

    const toggleEditField = (fieldName: string) => {
      if (setEditField) {
        setEditField(fieldName)
      }
    }

    const handleImageChange = (newValue: string) => {
      if (onChange) {
        onChange({ target: { name, value: newValue } });
      }
    }

    const isFieldInEditMode = isEditPage && editField === name;
    const filteredUploadedResources = (uploadedResources || []).filter((resource: any) => !removedResourceIds?.includes(resource.id));
    const maxVisibleResources = 5;
    const visibleUploadedResources = isFieldInEditMode
      ? filteredUploadedResources
      : filteredUploadedResources.slice(0, maxVisibleResources);
    const remainingResourceCount = isFieldInEditMode
      ? 0
      : filteredUploadedResources.length - visibleUploadedResources.length;

    const renderUploadedResourceBox = (resource: any, index: number) => (
      <Box key={resource.id || index} sx={{ position: 'relative' }}>
        <Box
          sx={{
            width: `${imageWidth}px`,
            height: `${imageHeight}px`,
            backgroundColor: 'grey.50',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid',
            borderColor: 'grey.200',
            cursor: 'pointer',
            '&:hover': {
              borderColor: 'grey.400',
              backgroundColor: 'grey.200',
            }
          }}
          onClick={async () => {
            if (isEditPage && resource.id) {
              const service = new UploadedResourceService();
              await service.downloadFile(resource.id, resource.filename);
            } else if (resource.filepath) {
              window.open(resource.filepath, '_blank', 'noopener,noreferrer');
            }
          }}
        >
          <UploadedResourcePreview
            resource={resource}
            width={imageWidth}
            height={imageHeight}
          />
          <Typography variant="caption" sx={{
            textAlign: 'center',
            wordBreak: 'break-word',
            fontSize: '0.7rem',
            lineHeight: 1.2,
            mt: 0.5,
            px: 0.5
          }}>
            {resource.filename.length > 12 ? resource.filename.substring(0, 12) + '...' : resource.filename}
          </Typography>
        </Box>
        {(isEditPage && !disabled && editField === name && onToggleResourceRemove) && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onToggleResourceRemove(resource.id, true);
            }}
            sx={{
              position: 'absolute',
              top: -8,
              right: -8,
              backgroundColor: 'text.secondary',
              color: 'common.white',
              width: '20px',
              height: '20px',
              display: 'flex',
              '&:hover': {
                backgroundColor: 'text.primary',
              }
            }}
          >
            <Close sx={{ fontSize: '12px' }} />
          </IconButton>
        )}
      </Box>
    );

    return (
      <FormControl fullWidth variant='outlined'>
        <Stack sx={{
          borderWidth: '1px',
          borderBottomStyle: bottomBorder || isBottomRow ? 'solid' : 'none',
          borderColor: 'lightGrey.main',
          borderLeftStyle: sideBorders ? 'solid' : 'none',
          borderRightStyle: sideBorders ? 'solid' : 'none',
          borderTopStyle: topBorder || isTopRow ? 'solid' : 'none',
          pt: 1, pb: 1, pl: 3,
          borderTopLeftRadius: isTopRow ? '10px' : 0,
          borderTopRightRadius: isTopRow ? '10px' : 0,
          borderBottomLeftRadius: isBottomRow ? '10px' : 0,
          borderBottomRightRadius: isBottomRow ? '10px' : 0
        }}>
          <Stack position='relative' alignItems={isMobileMode ? 'flex-start' : 'center'} flexDirection={isMobileMode ? 'column' : 'row'} justifyContent='start'>
            <Stack sx={{ width: isMobileMode ? '100%' : '40%', minWidth: '100px', color: 'text.primary', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <Typography variant='subtitle1'> {title} </Typography>
              {tooltip && !isMobileMode && <TooltipButton
                text={tooltip}
                noPadding={true}
                placement='bottom' />}
            </Stack>
            <Stack position='relative' flexDirection='column' sx={{ width: '100%' }}>
              <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'>
                <Stack direction={isMobileMode ? 'column' : 'row'} alignItems={isMobileMode ? 'flex-start' : 'center'} sx={{ width: '100%' }}>
                  {filteredUploadedResources.length > 0 ? (
                    isFieldInEditMode ? (
                      <Stack
                        spacing={2}
                        sx={{
                          mr: 3,
                          mb: isMobileMode ? 2 : 0,
                          ml: 2,
                          maxWidth: '100%',
                        }}
                      >
                        {Array.from(
                          { length: Math.ceil(filteredUploadedResources.length / maxVisibleResources) },
                          (_, rowIndex) => {
                            const start = rowIndex * maxVisibleResources;
                            const end = start + maxVisibleResources;
                            const rowItems = filteredUploadedResources.slice(start, end);
                            return (
                              <Stack key={rowIndex} direction="row" spacing={2}>
                                {rowItems.map((resource, index) =>
                                  renderUploadedResourceBox(resource, start + index)
                                )}
                              </Stack>
                            );
                          }
                        )}
                      </Stack>
                    ) : (
                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{
                          mr: 3,
                          mb: isMobileMode ? 2 : 0,
                          ml: 2,
                          flexWrap: 'nowrap',
                          overflowX: 'hidden',
                          maxWidth: '100%',
                        }}
                      >
                        {visibleUploadedResources.map((resource, index) =>
                          renderUploadedResourceBox(resource, index)
                        )}
                        {remainingResourceCount > 0 && (
                          <Box sx={{ position: 'relative' }}>
                            <Box
                              sx={{
                                width: `${imageWidth}px`,
                                height: `${imageHeight}px`,
                                backgroundColor: 'grey.50',
                                borderRadius: '8px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px solid',
                                borderColor: 'grey.200',
                              }}
                            >
                              <Typography variant="h6">
                                ...
                              </Typography>
                              <Typography variant="caption" sx={{
                                textAlign: 'center',
                                wordBreak: 'break-word',
                                fontSize: '0.7rem',
                                lineHeight: 1.2,
                                mt: 0.5,
                                px: 0.5
                              }}>
                                {`+${remainingResourceCount} more`}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Stack>
                    )
                  ) : value ? (
                    <Box sx={{ mr: 3, mb: isMobileMode ? 2 : 0, ml: 2 }}>
                      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', '&:hover .remove-button': { display: 'flex' } }}>
                        <InsertDriveFileOutlined sx={{ mr: 1 }} />
                        <Link href={value} target='_blank' rel='noopener noreferrer' underline='hover'>
                          View file
                        </Link>
                        {onImageRemove && !disabled && (
                          <IconButton
                            onClick={() => {
                              onImageRemove();
                              handleImageChange('');
                            }}
                            className="remove-button"
                            sx={{
                              ml: 1,
                              backgroundColor: 'text.secondary',
                              color: 'common.white',
                              width: '20px',
                              height: '20px',
                              display: 'none',
                              '&:hover': {
                                backgroundColor: 'text.primary',
                              }
                            }}
                          >
                            <Close sx={{ fontSize: '12px' }} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  ) : (fileData && fileData.length > 0) ? null : (
                    <Box sx={{ mr: 3, mb: isMobileMode ? 2 : 0, ml: 2 }}>
                      <Box sx={{
                        width: `${imageWidth}px`,
                        height: `${imageHeight}px`,
                        backgroundColor: 'primary.light',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {placeholderIcon || <InsertDriveFileOutlined sx={{ color: 'common.white', fontSize: '2rem' }} />}
                      </Box>
                    </Box>
                  )}
                  {(isEditPage && disabled) ? null : (
                    <Stack direction='row' spacing={2} alignItems='center' sx={{ ml: (fileData && fileData.length > 0 && !value) ? 2 : 0 }}>
                      {fileData && setFileData && onFileUploadSubmit ? (
                        <FileUploadHelper
                          disableButton={disabled}
                          fileData={fileData}
                          setFileData={setFileData}
                          buttonCopy={uploadButtonText}
                          onSubmit={onFileUploadSubmit}
                          hidePreview={hidePreview}
                          allowedFileTypes={allowedFileTypes}
                        />
                      ) : onImageUpload ? (
                        <Button
                          variant="outlined"
                          onClick={onImageUpload}
                          disabled={disabled}
                          sx={{ maxHeight: '44px' }}
                        >
                          <UploadOutlined />
                          {uploadButtonText}
                        </Button>
                      ) : null}
                      {showImageLibraryButton && onImageLibrarySelect && (
                        <Button
                          variant="text"
                          onClick={onImageLibrarySelect}
                          disabled={disabled}
                          sx={{ maxHeight: '44px' }}
                        >
                          <InsertDriveFileOutlined />
                          <Typography sx={{ p: 0.5 }}>{imageLibraryButtonText}</Typography>
                        </Button>
                      )}
                    </Stack>
                  )}
                </Stack>
                {allowEdit &&
                  <FormEditButtons
                    isEditPage={isEditPage}
                    editField={editField}
                    hideEditIcon={hideEditIcon}
                    name={name}
                    toggleEditField={toggleEditField}
                    cancelEditHandler={cancelEditHandler}
                  />}
              </Stack>
              {touched && errors &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {errors ? <div>{errors as string}</div> : null}
                </FormHelperText>
              }
              {customErrorMessage &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{customErrorMessage}
                </FormHelperText>
              }
            </Stack>
          </Stack>
        </Stack>
      </FormControl>
    )
  }